import React, { useEffect, useState } from "react";
import { Tabs, Table, Input, Statistic, Select, message, Tag } from "antd";
import { GlobalOutlined, BankOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  AreaWrapper,
  ToolbarRow,
  TableContainer,
  StatsRow,
  StatCard,
} from "./styled";
import {
  getAreaProvinces,
  getAreaHospitals,
  getAreaSummary,
} from "../../api/area";

const { Search } = Input;

// Names in the database are stored decomposed (NFD) while typed text is NFC,
// so compare both in the same form
const normalize = (value) =>
  String(value ?? "").normalize("NFC").toLowerCase();
const matches = (text, search) => normalize(text).includes(normalize(search));

const ProvincesTab = () => {
  const { t } = useTranslation();
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const res = await getAreaProvinces();
        setProvinces(res?.provinces || []);
      } catch (e) {
        message.error(t("area.loadError"));
      } finally {
        setLoading(false);
      }
    };
    fetchProvinces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = provinces.filter(
    (p) =>
      matches(p.name, search) ||
      matches(p.code, search) ||
      p.merged_from.some((m) => matches(m.name, search))
  );

  const columns = [
    {
      title: t("area.code"),
      dataIndex: "code",
      key: "code",
      width: 70,
      sorter: (a, b) => a.code - b.code,
    },
    {
      title: t("area.name"),
      dataIndex: "name",
      key: "name",
      width: 200,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: t("area.hospitalCount"),
      dataIndex: "hospital_count",
      key: "hospital_count",
      width: 120,
      sorter: (a, b) => a.hospital_count - b.hospital_count,
    },
    {
      title: t("area.mergedFrom"),
      key: "merged_from",
      render: (_, record) =>
        record.merged_from.map((m) => (
          <Tag key={m.code}>
            {m.name} ({m.code})
          </Tag>
        )),
    },
  ];

  return (
    <>
      <ToolbarRow>
        <Search
          placeholder={t("area.searchProvince")}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 250 }}
          allowClear
        />
        <span style={{ marginLeft: "auto", color: "#888" }}>
          {provinces.length} {t("area.total")}
        </span>
      </ToolbarRow>
      <TableContainer>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="code"
          size="small"
          loading={loading}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: ["20", "50", "100"],
          }}
        />
      </TableContainer>
    </>
  );
};

const HospitalsTab = () => {
  const { t } = useTranslation();
  const [hospitals, setHospitals] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterProvince, setFilterProvince] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [hospRes, provRes] = await Promise.all([
          getAreaHospitals(filterProvince),
          getAreaProvinces(),
        ]);
        setHospitals(hospRes?.hospitals || []);
        setProvinces(provRes?.provinces || []);
      } catch (e) {
        message.error(t("area.loadError"));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProvince]);

  const filtered = hospitals.filter(
    (h) => matches(h.name, search) || matches(h.code, search)
  );

  const columns = [
    {
      title: t("area.code"),
      dataIndex: "code",
      key: "code",
      width: 90,
      sorter: (a, b) => a.code - b.code,
    },
    {
      title: t("area.name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: t("area.province"),
      dataIndex: "province_name",
      key: "province_name",
      width: 200,
      sorter: (a, b) =>
        (a.province_name || "").localeCompare(b.province_name || ""),
    },
  ];

  return (
    <>
      <ToolbarRow>
        <Search
          placeholder={t("area.searchHospital")}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 250 }}
          allowClear
        />
        <Select
          style={{ width: 200 }}
          placeholder={t("area.filterByProvince")}
          allowClear
          showSearch
          optionFilterProp="children"
          onChange={(val) => setFilterProvince(val)}
          value={filterProvince}
        >
          {provinces.map((p) => (
            <Select.Option key={p.code} value={p.code}>
              {p.name}
            </Select.Option>
          ))}
        </Select>
        <span style={{ marginLeft: "auto", color: "#888" }}>
          {hospitals.length} {t("area.total")}
        </span>
      </ToolbarRow>
      <TableContainer>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="code"
          size="small"
          loading={loading}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />
      </TableContainer>
    </>
  );
};

const AreaManagementPage = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await getAreaSummary();
        setSummary(res);
      } catch (e) {}
    };
    fetchSummary();
  }, []);

  return (
    <AreaWrapper>
      {summary && (
        <StatsRow>
          <StatCard>
            <Statistic
              title={t("area.totalProvinces")}
              value={summary.total_provinces}
            />
          </StatCard>
          <StatCard>
            <Statistic
              title={t("area.totalHospitals")}
              value={summary.total_hospitals}
            />
          </StatCard>
          <StatCard>
            <Statistic
              title={t("area.mergedProvinces")}
              value={summary.merged_provinces}
            />
          </StatCard>
        </StatsRow>
      )}

      <Tabs
        defaultActiveKey="1"
        size="large"
        items={[
          {
            key: "1",
            label: (
              <span>
                <GlobalOutlined /> {t("area.provinces")}
              </span>
            ),
            children: <ProvincesTab />,
          },
          {
            key: "2",
            label: (
              <span>
                <BankOutlined /> {t("area.hospitals")}
              </span>
            ),
            children: <HospitalsTab />,
          },
        ]}
      />
    </AreaWrapper>
  );
};

export default AreaManagementPage;
